import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import { Check, LoaderCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { isElectronDesktop } from '@/lib/electron-bridge';
import { useRouter } from '@/lib/navigation';
import { pluginPagePath, pluginsPath } from '@/lib/plugins/plugin-sections';
import { pushPermission, pushSupport, subscribeToPush } from '@/lib/push-notifications';
import {
  fetchSourceControlRepositories,
  type SourceControlConnectorState,
} from '@/lib/source-control-connector';

/** The steps of the first launch: the desktop app's (D23) and the phone's (mobile 20). */
export type FirstLaunchStep = 'github' | 'agents' | 'repository' | 'install' | 'notifications';

export function firstLaunchSteps(phone: boolean): readonly FirstLaunchStep[] {
  return phone
    ? ['github', 'repository', 'install', 'notifications']
    : ['github', 'agents', 'repository', 'notifications'];
}

/** The step to do now: the first neither done nor skipped (`null`: all of them are). */
export function currentFirstLaunchStep(
  steps: readonly FirstLaunchStep[],
  done: ReadonlySet<FirstLaunchStep>,
  skipped: ReadonlySet<FirstLaunchStep>,
): FirstLaunchStep | null {
  return steps.find((step) => !done.has(step) && !skipped.has(step)) ?? null;
}

const SEEN_KEY = 'crowdex.firstLaunch.seen';

function seen(): boolean {
  try {
    return globalThis.localStorage?.getItem(SEEN_KEY) === 'true';
  } catch {
    return true;
  }
}

function markSeen(): void {
  try {
    globalThis.localStorage?.setItem(SEEN_KEY, 'true');
  } catch {}
}

/** The app runs from the home screen (needed for a phone's notifications). */
function installed(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

const PREVIEW_FORGE: SourceControlConnectorState = {
  configured: true,
  connected: true,
  providers: [{ name: 'github', title: 'GitHub', connected: true }],
  repositories: [
    {
      host: 'github.com',
      provider: 'github',
      fullName: 'PersoJonas/crowdex',
      name: 'crowdex',
      owner: 'PersoJonas',
      private: true,
      defaultBranch: 'main',
      url: 'https://github.com/PersoJonas/crowdex',
      cloneUrl: 'https://github.com/PersoJonas/crowdex.git',
      description: null,
      workspaceRoot: '/workspace/github.com/PersoJonas/crowdex',
    },
  ],
};

/**
 * The first launch (D23, mobile 20): four steps in order (connect GitHub, the agents found
 * here or adding to the home screen, a repository, notifications), each skippable, the whole
 * of it too ("Skip for now"). Shown once; `/desktop-preview/welcome` shows it in the preview.
 */
export function FirstLaunch({
  phone,
  preview,
  agentNames,
  discoveringAgents,
  onChooseRepository,
}: {
  phone: boolean;
  preview: boolean;
  /** The agents found on this computer. */
  agentNames: readonly string[];
  discoveringAgents: boolean;
  /** Opens "Add a repository" (D23b). */
  onChooseRepository: () => void;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(() =>
    preview ? window.location.pathname === '/desktop-preview/welcome' : !seen(),
  );
  const [forge, setForge] = useState<SourceControlConnectorState | null>(
    preview ? PREVIEW_FORGE : null,
  );
  const [done, setDone] = useState<ReadonlySet<FirstLaunchStep>>(new Set());
  const [skipped, setSkipped] = useState<ReadonlySet<FirstLaunchStep>>(new Set());
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (!open || preview) return;
    let cancelled = false;
    void fetchSourceControlRepositories().then(
      (state) => !cancelled && setForge(state),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [open, preview]);

  const steps = firstLaunchSteps(phone);
  const reached = new Set(done);
  if (forge?.connected) reached.add('github');
  if (!phone && agentNames.length > 0) reached.add('agents');
  if (installed()) reached.add('install');
  if (!preview && pushPermission() === 'granted') reached.add('notifications');
  const current = currentFirstLaunchStep(steps, reached, skipped);
  if (!open) return null;

  const close = () => {
    if (!preview) markSeen();
    setOpen(false);
  };
  const complete = (step: FirstLaunchStep) => setDone((items) => new Set(items).add(step));
  const owner = forge?.repositories[0]?.owner;

  const copy: Record<FirstLaunchStep, { title: string; detail: string; action?: string }> = {
    github: {
      title: 'Connect GitHub',
      detail: forge?.connected
        ? owner
          ? `Connected as ${owner}`
          : 'Connected'
        : 'Your repositories and pull requests',
      action: 'Connect',
    },
    agents: {
      title: 'Agents found on this computer',
      detail: discoveringAgents
        ? 'Looking for agents…'
        : agentNames.length > 0
          ? agentNames.join(', ')
          : 'None yet: install Claude Code, Codex or another ACP agent',
      action: 'Review',
    },
    repository: {
      title: 'Pick a repository',
      detail: 'Where your first conversation runs',
      action: 'Choose a repository',
    },
    install: {
      title: 'Add to Home Screen',
      detail: 'Share › Add to Home Screen. Needed for notifications on iPhone.',
    },
    notifications: {
      title: 'Allow notifications',
      detail: 'Know when an agent needs you',
      action: 'Allow',
    },
  };

  const act = (step: FirstLaunchStep) => {
    if (step === 'github') {
      const provider = forge?.providers[0];
      router.push(provider ? pluginPagePath('installed', provider.name) : pluginsPath());
      return;
    }
    if (step === 'agents') {
      complete('agents');
      router.push('/settings?section=agents');
      return;
    }
    if (step === 'repository') {
      complete('repository');
      onChooseRepository();
      return;
    }
    if (step === 'install') {
      complete('install');
      return;
    }
    setPending(true);
    const ask: Promise<unknown> = preview
      ? Promise.resolve()
      : pushSupport() === 'supported'
        ? subscribeToPush()
        : isElectronDesktop() && 'Notification' in window
          ? Notification.requestPermission()
          : Promise.resolve();
    void ask
      .catch(() => undefined)
      .finally(() => {
        setPending(false);
        complete('notifications');
      });
  };

  return (
    <div
      aria-labelledby="first-launch-title"
      aria-modal
      className="fixed inset-0 z-modal flex overflow-y-auto bg-background text-foreground"
      role="dialog"
    >
      <div
        className={cn(
          'mx-auto flex w-full flex-col',
          phone
            ? 'px-5 pt-[max(4rem,env(safe-area-inset-top))] pb-8'
            : 'max-w-[35rem] justify-center px-6 py-16',
        )}
      >
        <h1 className="type-page-title" id="first-launch-title">
          Welcome to Crowdex
        </h1>
        <p className="type-body mt-2 text-muted-foreground">
          {phone
            ? 'Your agents keep working in the cloud. Follow them, answer them and review their work from here.'
            : 'Your agents keep working on this computer or in the cloud. Follow them, answer them and review their work from here.'}
        </p>
        <ol className="mt-6 flex flex-col gap-3">
          {steps.map((step, index) => {
            const isDone = reached.has(step);
            const isCurrent = step === current;
            const later = !isDone && !isCurrent && !skipped.has(step);
            const { title, detail, action } = copy[step];
            // The agents found stay reviewable once found.
            const showAction =
              action !== undefined && (!isDone || step === 'agents') && (!phone || isCurrent);
            return (
              <li
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  'flex flex-col gap-3 rounded-2xl border bg-card px-4 py-3.5',
                  phone && isCurrent && 'border border-foreground/70',
                  phone && later && 'opacity-55',
                )}
                key={step}
              >
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden
                    className={cn(
                      'type-meta flex size-7 shrink-0 items-center justify-center rounded-full',
                      isDone
                        ? 'bg-success text-background'
                        : 'border border-hairline-strong text-muted-foreground',
                      isCurrent && phone && 'border-foreground text-foreground',
                    )}
                  >
                    {isDone ? <Check className="size-4" strokeWidth={3} /> : index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="type-body block font-semibold">{title}</span>
                    <span className="type-meta block text-muted-foreground">{detail}</span>
                  </span>
                  {showAction && !phone && (
                    <Button
                      className="h-8 rounded-control px-3.5"
                      disabled={pending && step === 'notifications'}
                      onClick={() => act(step)}
                      variant={isCurrent ? 'default' : 'outline'}
                    >
                      {pending && step === 'notifications' && (
                        <LoaderCircle className="size-3.5 animate-spin" />
                      )}
                      {action}
                    </Button>
                  )}
                </div>
                {phone && isCurrent && (
                  <div className="flex flex-col gap-2">
                    {action !== undefined && (
                      <Button
                        className="h-12 rounded-control text-base"
                        disabled={pending}
                        onClick={() => act(step)}
                      >
                        {action}
                      </Button>
                    )}
                    <Button
                      className="h-9 text-muted-foreground"
                      onClick={() =>
                        action === undefined
                          ? complete(step)
                          : setSkipped((items) => new Set(items).add(step))
                      }
                      variant="ghost"
                    >
                      {action === undefined ? 'Done' : 'Skip'}
                    </Button>
                  </div>
                )}
                {!phone && isCurrent && !isDone && (
                  <button
                    className="type-meta -mt-1 self-start pl-10 text-muted-foreground hover:text-foreground"
                    onClick={() => setSkipped((items) => new Set(items).add(step))}
                    type="button"
                  >
                    Skip
                  </button>
                )}
              </li>
            );
          })}
        </ol>
        <button
          className={cn(
            'type-meta mt-4 text-muted-foreground hover:text-foreground',
            phone ? 'type-body mt-auto self-center pt-10' : 'self-start',
          )}
          onClick={close}
          type="button"
        >
          {current === null ? 'Start' : 'Skip for now'}
        </button>
      </div>
    </div>
  );
}
